/**
 * Serialize / restore /icr list filters + sort in the query string so Back from
 * an ICR/EEI detail page can reopen the same filtered, sorted table / overview.
 */

const DEFAULT_TAB = 'overview';
const DEFAULT_SORT = {by: 'overlap', dir: 'desc'};
/** Default Status multiselect: Active only (hide Expired / Proposed). */
export const DEFAULT_STATUS = ['active'];
/** Default Gap status multiselect: all lifecycle values. */
export const DEFAULT_GAP_STATUS = ['identified', 'proposed', 'promoted'];

const VALID_TABS = new Set(['overview', 'icr', 'eei', 'gap']);

function normalizeStatusList(raw) {
    const values = (Array.isArray(raw) ? raw : (raw ? [raw] : []))
        .map((s) => String(s).trim().toLowerCase())
        .filter(Boolean);
    return [...new Set(values)];
}

export function isDefaultStatus(status) {
    const values = normalizeStatusList(status);
    return values.length === 1 && values[0] === 'active';
}

export function isDefaultGapStatus(status) {
    const values = normalizeStatusList(status);
    if (values.length !== DEFAULT_GAP_STATUS.length) return false;
    return DEFAULT_GAP_STATUS.every((s) => values.includes(s));
}

export function isDefaultStatusForTab(tab, status) {
    return tab === 'gap' ? isDefaultGapStatus(status) : isDefaultStatus(status);
}

export function parseRequirementsListSearch(searchParams) {
    const params = searchParams instanceof URLSearchParams
        ? searchParams
        : new URLSearchParams(searchParams || '');

    const tabRaw = (params.get('tab') || '').toLowerCase();
    const tab = VALID_TABS.has(tabRaw) ? tabRaw : DEFAULT_TAB;

    const sortBy = params.get('sortBy') || DEFAULT_SORT.by;
    const sortDirRaw = (params.get('sortDir') || '').toLowerCase();
    const sortDir = sortDirRaw === 'asc' || sortDirRaw === 'desc'
        ? sortDirRaw
        : DEFAULT_SORT.dir;

    const tags = params.getAll('tag').map((t) => t.trim()).filter(Boolean);
    const owners = params.getAll('owner').map((o) => o.trim()).filter(Boolean);
    const statusParams = normalizeStatusList(params.getAll('status'));
    // Missing status → Active (ICR/EEI) or all gap statuses; explicit status params restore as-is.
    const status = statusParams.length
        ? statusParams
        : (tab === 'gap' ? [...DEFAULT_GAP_STATUS] : [...DEFAULT_STATUS]);

    return {
        tab,
        q: params.get('q') || '',
        tags,
        owners,
        priority: params.get('priority') || '',
        classification: params.get('classification') || '',
        status,
        sortBy,
        sortDir,
        filtersOpen: params.get('filters') === '1'
            || tags.length > 0
            || owners.length > 0
            || Boolean(params.get('priority'))
            || Boolean(params.get('classification'))
            // Default status alone should not force the filter panel open.
            || (statusParams.length > 0 && !isDefaultStatusForTab(tab, statusParams)),
    };
}

export function buildRequirementsListSearch({
    tab = DEFAULT_TAB,
    q = '',
    tags = [],
    owners = [],
    priority = '',
    classification = '',
    status = DEFAULT_STATUS,
    sortBy = DEFAULT_SORT.by,
    sortDir = DEFAULT_SORT.dir,
    filtersOpen = false,
} = {}) {
    const params = new URLSearchParams();
    if (tab === 'icr' || tab === 'eei' || tab === 'gap') params.set('tab', tab);
    const trimmedQ = (q || '').trim();
    if (trimmedQ) params.set('q', trimmedQ);
    (tags || []).forEach((tag) => {
        if (tag) params.append('tag', tag);
    });
    (owners || []).forEach((owner) => {
        if (owner) params.append('owner', owner);
    });
    if (priority) params.set('priority', priority);
    if (classification) params.set('classification', classification);
    normalizeStatusList(status).forEach((s) => params.append('status', s));
    const isDefaultSort = sortBy === DEFAULT_SORT.by && sortDir === DEFAULT_SORT.dir;
    if (sortBy && !isDefaultSort) {
        params.set('sortBy', sortBy);
        params.set('sortDir', sortDir || 'desc');
    }
    if (filtersOpen) params.set('filters', '1');
    return params;
}

export function requirementsListReturnTo(searchParams) {
    const qs = searchParams?.toString?.() || '';
    return qs ? `/icr?${qs}` : '/icr';
}
